import { useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { MediaSection, MediaItem } from 'components';

const isYouTubeUrl = url => /youtu\.be|youtube\.com/.test(url);
const toEmbedUrl = url => {
  try {
    const ytMatch = url.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([\w-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const shortMatch = url.match(/youtu\.be\/([\w-]+)/);
    if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
  } catch {}
  return url;
};

export default function AssetSection({
  adId,
  type,
  label,
  images = [],
  videos = [],
  previews = [],
  removedList = [],
  warning,
  onUpload,
  onRemove,
  editMode,
  mediaKey = 'images',
  acceptedFiles = 'image/*',
  uploadDescription = 'Formatos aceptados: JPG, PNG'
}) {
  const [mediaErrors, setMediaErrors] = useState({});
  const inputRef = useRef();
  const mediaList = mediaKey === 'images' ? images : videos;
  const enriched = mediaList.map((item, idx) => ({
    ...item,
    id: item.asset_group_resource_name || item.resource_name || item.url || `${mediaKey}_${idx}`
  }));
  const filtered = enriched.filter(item => !removedList.includes(item.id));
  const combined = [...filtered, ...previews.map(prev => ({ ...prev, id: prev.file_placeholder }))];

  const handleMediaError = (url, e) => {
    setMediaErrors(prev => ({ ...prev, [url]: e.type || 'error' }));
  };

  return (
    <div className="space-y-2 pt-4">
      <div className="text-xs font-semibold text-gray-500">{label}</div>
      {warning && (
        <div className="flex items-center gap-2 text-xs text-error-dark bg-error-bg px-3 py-2 rounded shadow-sm">
          <AlertTriangle size={14} />
          {warning}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {combined.map((item, idx) => {
          const isNew = Boolean(item.file);
          const url = item.url;
          if (isYouTubeUrl(url)) {
            return (
              <div
                key={`${type}-${item.id}-${idx}`}
                className="rounded-2xl overflow-hidden shadow-card border border-gray-200 bg-black aspect-video"
              >
                <iframe
                  src={toEmbedUrl(url)}
                  title={`${label} ${idx + 1}`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            );
          }
          return (
            <MediaItem
              key={`${type}-${item.id}-${idx}`}
              media={{ id: item.id, url }}
              index={idx}
              onDelete={editMode ? () => onRemove({ adId, type, id: item.id, isNew }) : null}
              mediaErrors={mediaErrors}
              handleMediaError={handleMediaError}
              setMediaErrors={setMediaErrors}
              forceVideo={mediaKey === 'videos'}
            />
          );
        })}
        {editMode && (
          <div className="w-full">
            <MediaSection
              fileInputRef={inputRef}
              handleInputChange={e => onUpload(adId, type, e.target.files)}
              title={`Agregar ${label}`}
              uploadText={`Sube ${label.toLowerCase()}`}
              uploadDescription={uploadDescription}
              type="generic"
              acceptedFiles={acceptedFiles}
              showTitle={false}
              forceVideo={mediaKey === 'videos'}
            />
          </div>
        )}
      </div>
    </div>
  );
}
